import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
  DeleteConfirmation,
  FiltersBar,
  IconLink,
  ListView,
  ListViewActions,
  ListViewMenu,
  Page,
  SearchInput,
  SingleSelectFilter,
  Tag,
  TagsList,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { QuerySort } from '~/utils';
import ProjectGroupForm from './ProjectGroupForm';
import ProjectGroupView from './ProjectGroupView';

export default function ProjectGroupsTab({ client }) {
  const documentTitle = useDocumentTitle();

  const { workspace } = useWorkspace();
  const api = useApi();
  const history = useHistory();
  const { projectGroupId, action } = useParams();
  const { url } = useRouteMatch();
  const [{ data, isReady, isFetching }, setQuery] = useState({ data: null, isReady: false, isFetching: false });
  const toast = useToast();
  const confirmation = useConfirmation();

  const [params, setParams] = useState({
    q: '',
    isActive: 'true',
    sort: new QuerySort('name', 'asc'),
  });

  const fetchData = useCallback(async () => {
    setQuery((query) => ({ ...query, isFetching: true }));

    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectGroups()
        .get({ q: params.q, clientId: client.id, isActive: params.isActive ?? undefined });
      setQuery({ data, isReady: true, isFetching: false });
    } catch (error) {
      setQuery({ data: null, isReady: true, isFetching: false });
    }
  }, [workspace.id, api, params, client.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = ({ target: { name, value } }) => {
    setQuery((query) => ({ ...query, isFetching: true }));
    setParams((params) => ({ ...params, [name]: value }));
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    setQuery((state) => ({ ...state, status: 'filtering' }));
    setParams((state) => ({ ...state, sort: new QuerySort(column, direction) }));
  };

  function handleClose() {
    history.push(`/app/${workspace.key}/portfolio/clients/${client.key}/project-groups`);
    documentTitle.set(client.name);
  }

  const handleDeleteConfirmation = async (projectGroup) => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this project group?</DeleteConfirmation>
    ));
    if (!confirm) return;
    await api.www.workspaces(workspace.id).projectGroups(projectGroup.id).delete();
    await handleDeleted();
  };

  async function handleSaved() {
    fetchData();
  }

  async function handleDeleted() {
    fetchData();
    handleClose();
  }

  async function handleCreated() {
    fetchData();
  }

  async function handleActiveStatusChange(projectGroup, flag) {
    try {
      await api.www.workspaces(workspace.id).projectGroups(projectGroup.id).setActiveStatus(flag);
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'There was a problem updating the project group status. Please try again.');
    }
  }

  const handleView = ({ id }) => {
    history.push(`/app/${workspace.key}/portfolio/clients/${client.key}/project-groups/${id}/view`);
  };

  const handleEdit = ({ id }) => {
    history.push(`/app/${workspace.key}/portfolio/clients/${client.key}/project-groups/${id}/edit`);
  };

  if (!isReady) return <PageLoader />;
  if (!data) return <ErrorPage.NotFound publicSite={false} />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Project Groups</Page.Title>

        <Page.Actions>
          <IconLink icon="plus" tooltip="New Project Group" to={`${url}/new`} />
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleChange} />

          <SingleSelectFilter
            icon="filter"
            name="isActive"
            value={params.isActive}
            options={[
              { id: 'true', name: 'Active' },
              { id: 'false', name: 'Inactive' },
            ]}
            placeholder="Status"
            onChange={handleChange}
          />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column sticky minWidth="10rem" name="name" onSort={handleSort} sort={params.sort}>
              Name
            </ListView.Column>
            <ListView.Column minWidth="10rem">Description</ListView.Column>
            <ListView.Column minWidth="15rem">Projects</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body fade={isFetching}>
            {data.map((projectGroup) => {
              const { id, isActive, name, description, permissions, projects } = projectGroup;

              async function handleRowClick(projectGroup) {
                if (permissions.manage) {
                  handleEdit(projectGroup);
                } else {
                  handleView(projectGroup);
                }
              }

              return (
                <ListView.Row isDisabled={!isActive} key={id} onClick={() => handleRowClick(projectGroup)}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListView.Cell>{description}</ListView.Cell>
                  <ListView.Cell>
                    <TagsList>
                      {projects.map((project) => (
                        <Tag key={project.id}>{project.name}</Tag>
                      ))}
                    </TagsList>
                  </ListView.Cell>

                  <ListViewActions>
                    {permissions.manage ? (
                      <ListViewActions.Edit onClick={() => handleEdit(projectGroup)} />
                    ) : (
                      <ListViewActions.View onClick={() => handleView(projectGroup)} />
                    )}

                    <hr />

                    <ListViewMenu>
                      {({ setIsOpen }) => {
                        const handleAction = (action) => setIsOpen(false) || action;

                        return (
                          <>
                            <ListViewMenu.Item onClick={() => handleAction(handleView(projectGroup))}>
                              View
                            </ListViewMenu.Item>

                            <ListViewMenu.Item
                              disabled={!permissions.manage}
                              tooltip={
                                !permissions.manage ? 'Insufficient permissions to edit this project group.' : undefined
                              }
                              onClick={() => handleAction(handleEdit(projectGroup))}>
                              Edit
                            </ListViewMenu.Item>

                            <ListViewMenu.DeleteItem
                              disabled={!permissions.manage}
                              onCheckDependencies={async (workspace) =>
                                (await workspace.projectGroups(id).hasDependencies()).data
                              }
                              tooltip={
                                !permissions.manage
                                  ? 'Insufficient permissions to delete this project group.'
                                  : 'This item is currently in use.'
                              }
                              onClick={() => handleAction(handleDeleteConfirmation(projectGroup))}>
                              Delete
                            </ListViewMenu.DeleteItem>

                            <ListViewMenu.Item
                              disabled={!permissions.manage}
                              tooltip={
                                !permissions.manage
                                  ? `Insufficient permissions to ${isActive ? 'deactivate' : 'activate'} this project group.`
                                  : undefined
                              }
                              onClick={() => handleAction(handleActiveStatusChange(projectGroup, !isActive))}>
                              {isActive ? 'Deactivate' : 'Activate'}
                            </ListViewMenu.Item>
                          </>
                        );
                      }}
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Project Group" isLoading={isFetching} />
        </ListView>
      </Page.ListView>

      {action &&
        {
          new: <ProjectGroupForm clientId={client.id} onSaved={handleCreated} onClose={handleClose} />,
          edit: projectGroupId && (
            <ProjectGroupForm
              projectGroupId={projectGroupId}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
              onClose={handleClose}
            />
          ),
          view: projectGroupId && <ProjectGroupView projectGroupId={projectGroupId} onClose={handleClose} />,
        }[action]}
    </>
  );
}
